import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import MainLayout from '../../components/layout/MainLayout';
import { getTenantProfile, getTenantSupport, getTenantUsage } from '../../api/backendApi';
import BillingPage from './BillingPage';
import { normalizeSupportTab } from './supportNav';
import { canOpenSupportWorkspace, supportTabAllowed } from './supportWorkspaceAccess';
import { useAuth } from '../../contexts/AuthContext';
import { getAskSbEntitlement, putAskSbEntitlement } from '../../services/askSb/askSbApi';
import { currentUtcMonth, formatUtc } from './billingFormat';

export const TENANT_DETAIL_ALLOWED_ROLES = ['superadmin'];

function unwrap(response) {
  if (response && response.success === true && response.data) return response.data;
  return response || {};
}

function measurementLabel(value) {
  if (value === 'measured') return 'Measured';
  if (value === 'partial' || value === 'partial_estimate') return 'Partial';
  return 'Unavailable';
}

function MeasurementBadge({ value }) {
  return <span data-testid={`badge-${value || 'unavailable'}`}>{measurementLabel(value)}</span>;
}

function formatBytes(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return '—';
  const value = Number(bytes);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900">{value || '—'}</dd>
    </div>
  );
}

export function AskSbEntitlementControl({ tenantId }) {
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!tenantId) return undefined;
    let cancelled = false;
    getAskSbEntitlement(tenantId)
      .then((data) => {
        if (!cancelled) setEnabled(Boolean(data?.enabled));
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Could not load the entitlement');
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function save(next) {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await putAskSbEntitlement(tenantId, next);
      setEnabled(next);
      setMessage(next ? 'Ask Signal Bridges is enabled for this tenant.' : 'Ask Signal Bridges is off for this tenant.');
    } catch (err) {
      setError(err?.message || 'Could not save the entitlement');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mt-4 rounded border border-gray-200 bg-white p-4" data-testid="ask-sb-entitlement">
      <h2 className="text-sm font-semibold text-gray-900">Ask Signal Bridges</h2>
      <p className="mt-1 text-sm text-gray-600">
        Only a superadmin can enable Ask Signal Bridges for a tenant. A tenant admin cannot enable it.
      </p>
      <label className="mt-3 flex items-center gap-2 text-sm text-gray-900">
        <input
          type="checkbox"
          role="switch"
          aria-label="Ask Signal Bridges entitlement"
          checked={enabled}
          disabled={saving || !tenantId}
          onChange={(event) => save(event.target.checked)}
        />
        Ask Signal Bridges enabled
      </label>
      {message ? <p className="mt-2 text-sm text-gray-700">{message}</p> : null}
      {error ? <p className="mt-2 text-sm text-red-700">{error}</p> : null}
    </section>
  );
}

const CONFIG_LABELS = {
  mode: 'Mode',
  country: 'Country',
  isoCountry: 'Country',
  enableOutboundPstn: 'Outbound PSTN',
  voiceMode: 'Voice mode',
  callerIdFrom: 'Caller ID',
  fromNumber: 'From number',
  phoneNumber: 'Phone number',
  domainFqdn: 'SIP domain',
  domain: 'SIP domain',
};

function isSecretKey(key) {
  return /secret|token|password|credential/i.test(key);
}

function isTechnicalKey(key) {
  return isSecretKey(key) || /sid$|uid|userId|uri|url|arn|uuid|^id$/i.test(key);
}

function displayConfigValue(key, value) {
  if (isSecretKey(key)) return value ? 'configured' : 'not configured';
  return String(value);
}

function ConfigBlock({ title, data, empty }) {
  const entries = Object.entries(data || {}).filter(([, value]) => value !== null && value !== '' && typeof value !== 'object');
  const readable = entries.filter(([key]) => !isTechnicalKey(key));
  const technical = entries.filter(([key]) => isTechnicalKey(key));
  return (
    <section className="rounded border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {readable.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">{empty}</p>
      ) : (
        <dl className="mt-2 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          {readable.map(([key, value]) => (
            <Field key={key} label={CONFIG_LABELS[key] || key} value={displayConfigValue(key, value)} />
          ))}
        </dl>
      )}
      {technical.length ? (
        <details className="mt-3" data-testid="config-technical">
          <summary className="cursor-pointer text-sm text-gray-700">Technical details</summary>
          <dl className="mt-2 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            {technical.map(([key, value]) => (
              <Field key={key} label={isSecretKey(key) ? 'Stored secret' : (CONFIG_LABELS[key] || key)} value={displayConfigValue(key, value)} />
            ))}
          </dl>
        </details>
      ) : null}
    </section>
  );
}

export default function TenantDetailPage({
  embedded = false,
  tenantId: tenantIdProp,
  tab: tabProp,
  onTabChange,
  month,
  onMonthChange,
} = {}) {
  const params = useParams();
  const tenantId = tenantIdProp || params.tenantId;
  const { userClaims, userData } = useAuth();
  const role = userClaims?.role || userData?.role;
  const access = { role, permissions: userData?.permissions };
  const allowed = canOpenSupportWorkspace(access);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [localTab, setLocalTab] = useState('overview');
  const [showAgents, setShowAgents] = useState(false);
  const tab = embedded ? (normalizeSupportTab(tabProp) === 'data-usage' ? 'usage' : normalizeSupportTab(tabProp)) : localTab;
  const workspaceTab = tab === 'usage' ? 'data-usage' : tab;
  const tabAllowed = supportTabAllowed(access, workspaceTab);
  const [supportView, setSupportView] = useState(null);
  const [usageView, setUsageView] = useState(null);
  const [panelError, setPanelError] = useState('');
  const [panelLoading, setPanelLoading] = useState(false);
  const [supportAttempt, setSupportAttempt] = useState(0);
  const [usageAttempt, setUsageAttempt] = useState(0);

  async function loadPanel(which, cursor = '') {
    setPanelLoading(true);
    setPanelError('');
    try {
      if (which === 'support') {
        const response = await getTenantSupport(tenantId, { limit: 25, cursor });
        setSupportView(unwrap(response));
      } else {
        const response = await getTenantUsage(tenantId);
        setUsageView(unwrap(response));
      }
    } catch (err) {
      if (which === 'support') setSupportView(null);
      else setUsageView(null);
      setPanelError(err?.message || 'This view could not be loaded.');
    } finally {
      setPanelLoading(false);
    }
  }

  useEffect(() => {
    if (!allowed || !tabAllowed || !tenantId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await getTenantProfile(tenantId);
        if (cancelled) return;
        setProfile(unwrap(response));
      } catch (err) {
        if (cancelled) return;
        setProfile(null);
        setError(err?.message || 'Tenant profile could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, tabAllowed, tenantId]);

  useEffect(() => {
    if (!allowed || !tabAllowed || !tenantId || tab !== 'support' || supportAttempt > 0) return undefined;
    setSupportAttempt(1);
    loadPanel('support');
    return undefined;
  }, [allowed, tabAllowed, tenantId, tab, supportAttempt]);

  useEffect(() => {
    if (!allowed || !tabAllowed || !tenantId || tab !== 'usage' || usageAttempt > 0) return undefined;
    setUsageAttempt(1);
    loadPanel('usage');
    return undefined;
  }, [allowed, tabAllowed, tenantId, tab, usageAttempt]);

  function selectTab(value) {
    const next = value === 'usage' ? 'data-usage' : value;
    if (onTabChange) onTabChange(next);
    if (!embedded) setLocalTab(value === 'data-usage' ? 'usage' : value);
  }

  if (!allowed) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }} data-testid="tenant-detail-denied">
          <Typography variant="h5">Tenant details</Typography>
          <p className="mt-2 text-sm text-gray-600">You do not have permission to view this tenant.</p>
        </Box>
      </MainLayout>
    );
  }

  const overview = profile?.overview || {};
  const configuration = profile?.configuration || {};
  const billing = profile?.billing || {};
  const usage = profile?.dataUsage || {};
  const billingHref = `/admin/billing?tenantId=${encodeURIComponent(tenantId || '')}&month=${currentUtcMonth()}`;

  const body = (
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 }, maxWidth: 1100, mx: embedded ? 'none' : 'auto' }} data-testid="tenant-detail-page">
        {embedded ? null : (
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 650, color: '#111827' }}>
              {overview.name || tenantId}
            </Typography>
            <p className="mt-1 text-sm text-gray-600">{tenantId}</p>
          </div>
          <Link to="/admin/tenants" className="text-sm text-blue-700 hover:underline">
            Back to Tenant Management
          </Link>
        </div>
        )}

        {embedded ? null : (
        <Tabs value={tabAllowed ? tab : false} onChange={(_event, value) => selectTab(value)} sx={{ mt: 2 }}>
          {supportTabAllowed(access, 'overview') ? <Tab value="overview" label="Overview" /> : null}
          {supportTabAllowed(access, 'support') ? <Tab value="support" label="Support" /> : null}
          {supportTabAllowed(access, 'billing') ? <Tab value="billing" label="Billing" /> : null}
          {supportTabAllowed(access, 'data-usage') ? <Tab value="usage" label="Data Usage" /> : null}
          {supportTabAllowed(access, 'configuration') ? <Tab value="configuration" label="Configuration" /> : null}
        </Tabs>
        )}

        {!tabAllowed ? (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="tenant-tab-denied">
            You do not have permission to view this tab.
          </Alert>
        ) : null}

        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
        {loading ? <p className="mt-4 text-gray-600" data-testid="tenant-detail-loading">Loading tenant…</p> : null}

        {!loading && !error && tabAllowed && tab === 'overview' ? (
          <>
          <dl className="mt-4 grid grid-cols-2 gap-3 rounded border border-gray-200 bg-white p-4 text-sm md:grid-cols-3" data-testid="tenant-overview">
            <Field label="Status" value={overview.status} />
            <Field label="Created" value={formatUtc(overview.createdAt)} />
            <Field label="Deleted" value={formatUtc(overview.deletedAt)} />
            <Field label="Created by" value={overview.createdBy} />
            <Field label="Known measured records" value={String(usage.knownMeasuredRecords ?? '—')} />
          </dl>
          {role === 'superadmin' ? <AskSbEntitlementControl tenantId={tenantId} /> : null}
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              ['support', 'Support'],
              ['billing', 'Billing'],
              ['usage', 'Data Usage'],
              ['configuration', 'Configuration'],
            ].filter(([value]) => supportTabAllowed(access, value === 'usage' ? 'data-usage' : value)).map(([value, label]) => (
              <button key={value} type="button" className="rounded border border-gray-300 px-3 py-1 text-sm text-blue-800" onClick={() => selectTab(value)}>
                {label}
              </button>
            ))}
          </div>
          </>
        ) : null}

        {!loading && !error && tabAllowed && tab === 'support' ? (
          <section className="mt-4" data-testid="tenant-support">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-gray-600">Read-only support evidence. Last measured {formatUtc(supportView?.measuredAt)}.</p>
              <button type="button" className="text-sm text-blue-700 underline" data-testid="support-refresh" onClick={() => loadPanel('support', '')}>Refresh</button>
            </div>
            {panelLoading ? <p data-testid="support-loading">Loading support…</p> : null}
            {panelError ? <Alert severity="error" sx={{ mt: 2 }} data-testid="support-error">{panelError}</Alert> : null}
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              {(supportView?.cards || []).map((card) => (
                <article key={card.id} className="rounded border border-gray-200 bg-white p-3" data-testid={`support-card-${card.id}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">{card.label}</h3>
                    <MeasurementBadge value={card.measurement} />
                  </div>
                  <p className="mt-1 text-sm text-gray-900">{card.status}</p>
                  <p className="mt-1 text-xs text-gray-500">{card.explanation}</p>
                </article>
              ))}
            </div>
            {(supportView?.warnings || []).map((warning) => (
              <p key={warning.id} className="mt-2 text-sm text-amber-800" data-testid={`warning-${warning.id}`}>
                {warning.message}{' '}
                {supportTabAllowed(access, 'configuration') ? (
                  <button type="button" className="text-blue-700 underline" onClick={() => selectTab('configuration')}>Open configuration</button>
                ) : null}
              </p>
            ))}
            {['agents', 'devices'].map((key) => {
              const section = supportView?.[key];
              const rows = section?.rows || [];
              if (!rows.length) return null;
              const counts = section.counts || {};
              return (
                <div key={key} className="mt-4" data-testid={key === 'agents' ? 'agents-summary' : `${key}-table`}>
                  <h3 className="text-sm font-semibold capitalize">{key}</h3>
                  <p className="text-xs text-gray-500">
                    Total {section.total ?? '—'}. Recent {counts.recent || 0}. Heartbeat stale {counts['heartbeat stale'] || 0}. Unreachable {counts.unreachable || 0}. Unknown {counts.unknown || 0}.
                  </p>
                  {key === 'agents' ? (
                    <button type="button" className="mt-1 text-sm text-blue-700 underline" data-testid="agent-toggle" onClick={() => setShowAgents((open) => !open)}>
                      {showAgents ? 'Hide agents' : 'Show agents'}
                    </button>
                  ) : null}
                  {key === 'agents' && !showAgents ? null : (
                  <table className="mt-2 min-w-full text-left text-sm" data-testid={key === 'agents' ? 'agents-table' : undefined}>
                    <thead><tr>{(key === 'agents' ? ['Identifier', 'Name', 'Type', 'Agent health', 'Heartbeat', 'Cloud', 'MQTT', 'Auth', 'Paging', 'Last seen'] : ['Identifier', 'Name', 'Status', 'Last seen']).map((heading) => <th key={heading} className="px-2 py-1">{heading}</th>)}</tr></thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id} data-testid={key === 'agents' ? `support-agent-${row.id}` : undefined}>
                          <td className="px-2 py-1">{row.id}</td>
                          <td className="px-2 py-1">{row.name || '—'}</td>
                          {key === 'agents' ? (
                            <>
                              <td className="px-2 py-1">{row.agentType || '—'}</td>
                              <td className="px-2 py-1" data-testid={`support-health-${row.id}`}>
                                {row.effectiveHealth || '—'}
                                {row.freshness ? ` (${row.freshness === 'live' ? 'current' : row.freshness === 'last_known' ? 'last known' : row.freshness})` : ''}
                                {Array.isArray(row.reasons) && row.reasons.length ? ` — ${row.reasons.join(', ')}` : ''}
                              </td>
                              <td className="px-2 py-1">{row.status}{row.heartbeatAgeSeconds != null ? `, ${row.heartbeatAgeSeconds}s` : ''}</td>
                              <td className="px-2 py-1">{row.cloud || '—'}</td>
                              <td className="px-2 py-1">{row.mqtt || '—'}</td>
                              <td className="px-2 py-1">{row.authentication || '—'}</td>
                              <td className="px-2 py-1">{row.pagingAuthentication || '—'}</td>
                            </>
                          ) : (
                            <td className="px-2 py-1">{row.status}</td>
                          )}
                          <td className="px-2 py-1">{formatUtc(row.lastSeenAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  )}
                </div>
              );
            })}
            {Object.entries(supportView?.failures?.sources || {}).map(([name, source]) => (
              <div key={name} className="mt-3" data-testid={`failures-${name}`}>
                <h3 className="text-sm font-semibold">{name}</h3>
                <MeasurementBadge value={source.measurement} />
                {(source.items || []).length === 0 ? <p className="text-xs text-gray-500">{source.note || source.reason || 'No failed records in this sample.'}</p> : (
                  <table className="mt-1 min-w-full text-left text-sm">
                    <tbody>
                      {source.items.map((item) => (
                        <tr key={item.id}>
                          <td className="px-2 py-1">{formatUtc(item.at)}</td>
                          <td className="px-2 py-1">{item.id}</td>
                          <td className="px-2 py-1">{item.category}</td>
                          <td className="px-2 py-1">{item.summary || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
            {supportTabAllowed(access, 'billing') ? (
            <p className="mt-3 text-sm">
              <Link to={billingHref} className="text-blue-700 underline">Open Billing for vendor reporting</Link>
            </p>
            ) : null}
            <div className="mt-3 overflow-x-auto rounded border border-gray-200 bg-white">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    {['Title', 'Summary', 'Type', 'Status', 'Created', 'Updated', 'Reporter'].map((heading) => (
                      <th key={heading} className="px-3 py-2 font-medium">{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(supportView?.reports?.items || []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-4 text-gray-500">No support reports on this page.</td>
                    </tr>
                  ) : (
                    supportView.reports.items.map((report) => (
                      <tr key={report.id} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          <Link to={report.detailPath} className="text-blue-700 underline">{report.title}</Link>
                        </td>
                        <td className="px-3 py-2">{report.summary || '—'}</td>
                        <td className="px-3 py-2">{report.type || '—'}</td>
                        <td className="px-3 py-2">{report.status || '—'}</td>
                        <td className="px-3 py-2">{formatUtc(report.createdAt)}</td>
                        <td className="px-3 py-2">{formatUtc(report.updatedAt)}</td>
                        <td className="px-3 py-2">{report.reporter || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-gray-500">{supportView?.reports?.note}</p>
            {supportView?.reports?.nextCursor ? (
              <button type="button" className="mt-2 text-sm text-blue-700 underline" data-testid="support-next" onClick={() => loadPanel('support', supportView.reports.nextCursor)}>
                Next page
              </button>
            ) : null}
            {supportView?.tenantStatus === 'deleted' ? <p className="mt-2 text-sm text-gray-600">This tenant is deleted. This view is read-only.</p> : null}
          </section>
        ) : null}

        {!loading && !error && tabAllowed && tab === 'configuration' ? (
          <div className="mt-4 grid grid-cols-1 gap-3" data-testid="tenant-configuration">
            <p className="text-sm text-gray-600">{configuration.note}</p>
            <ConfigBlock title="SMS" data={configuration.sms} empty="No SMS settings stored on the tenant." />
            <ConfigBlock title="SIP" data={configuration.sip} empty="No SIP domain settings stored." />
            <ConfigBlock title="Paging" data={configuration.paging} empty="No paging settings document stored." />
            <ConfigBlock title="Logging" data={configuration.logging} empty="No logging settings stored." />
            <p className="text-sm text-gray-700" data-testid="webhook-secret">
              Webhook secret: {configuration.webhookSecretPresent ? 'Present (value hidden)' : 'Not stored'}
            </p>
          </div>
        ) : null}

        {!loading && !error && tabAllowed && tab === 'billing' ? (
          <section className="mt-4" data-testid="tenant-billing-report">
            <p className="mb-3 text-sm text-gray-700">{billing.note} Vendor costs stay on the Twilio report. This view does not calculate costs.</p>
            <BillingPage
              embedded
              lockedTenantId={tenantId}
              month={month || currentUtcMonth()}
              onMonthChange={onMonthChange}
            />
          </section>
        ) : null}

        {!loading && !error && tabAllowed && tab === 'usage' ? (
          <section className="mt-4" data-testid="tenant-data-usage">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-gray-600">Point-in-time data usage. Last measured {formatUtc(usageView?.measuredAt)}.</p>
              <button type="button" className="text-sm text-blue-700 underline" data-testid="usage-refresh" onClick={() => { setUsageAttempt(0); setUsageView(null); }}>Refresh</button>
            </div>
            {panelLoading ? <p data-testid="usage-loading">Loading data usage…</p> : null}
            {panelError ? <Alert severity="error" sx={{ mt: 2 }} data-testid="usage-error">{panelError}</Alert> : null}
            <div className="mt-3 overflow-x-auto rounded border border-gray-200 bg-white">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    {['Category', 'Records', 'Bytes', 'Measurement', 'Note'].map((heading) => (
                      <th key={heading} className="px-3 py-2 font-medium">{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(usageView?.categories || []).map((feature) => (
                    <tr key={feature.category} className="border-t border-gray-100" data-testid={`usage-${feature.category}`}>
                      <td className="px-3 py-2">{feature.category}</td>
                      <td className="px-3 py-2">{feature.measurement === 'unavailable' ? '—' : (feature.count ?? '—')}</td>
                      <td className="px-3 py-2">{feature.bytes == null ? '—' : formatBytes(feature.bytes)}</td>
                      <td className="px-3 py-2"><MeasurementBadge value={feature.measurement} /></td>
                      <td className="px-3 py-2 text-xs text-gray-500">{feature.note}</td>
                    </tr>
                  ))}
                  {(usageView?.storage || []).map((row) => (
                    <tr key={row.category} className="border-t border-gray-100" data-testid={`usage-storage-${row.category}`}>
                      <td className="px-3 py-2">{row.category}</td>
                      <td className="px-3 py-2">{row.measurement === 'measured' ? (row.objects ?? '—') : '—'}</td>
                      <td className="px-3 py-2">{row.measurement === 'measured' ? formatBytes(row.bytes) : '—'}</td>
                      <td className="px-3 py-2"><MeasurementBadge value={row.measurement} /></td>
                      <td className="px-3 py-2 text-xs text-gray-500">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Alert severity="info" sx={{ mt: 3 }} data-testid="usage-growth">
              {usageView?.growth?.message || 'Growth history unavailable — point-in-time measurements only.'}
            </Alert>
            {supportTabAllowed(access, 'billing') ? (
            <Link to={billingHref} className="mt-3 inline-block text-blue-700 underline" data-testid="usage-billing-link">
              Open Billing for vendor cost
            </Link>
            ) : null}
            <p className="mt-1 text-xs text-gray-500">{usageView?.billing?.note}</p>
            {usageView?.tenantStatus === 'deleted' ? <p className="mt-2 text-sm text-gray-600">This tenant is deleted. Counts are read-only.</p> : null}
          </section>
        ) : null}
      </Box>
  );
  if (embedded) return body;
  return <MainLayout scrollMain>{body}</MainLayout>;
}
